import { BrandKit } from "@redotech/redo-model/brand-kit";
import { IFaqTheme } from "@redotech/redo-model/faq/faq-body-schema";
import { IFaqCollection } from "@redotech/redo-model/faq/faq-db-parser";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { SettingsCard } from "@redotech/redo-web/arbiter-components/settings-card/settings-card";
import LeftArrowIcon from "@redotech/redo-web/arbiter-icon/arrow-left_filled.svg";
import RightArrowIcon from "@redotech/redo-web/arbiter-icon/arrow-right_filled.svg";
import UpLeftArrowIcon from "@redotech/redo-web/arbiter-icon/arrow-up-left_filled.svg";
import CubeIcon from "@redotech/redo-web/arbiter-icon/cube-01_outline.svg";
import { Flex } from "@redotech/redo-web/flex";
import { memo } from "react";
import { Header } from "../../text";
import { useIsMobile } from "../../utils/check-mobile";
import { FaqSearchView, FaqSearchViewProps } from "./faq-search";
import * as homeCss from "./home.module.css";

// Need to convert hex to rgba for the background image overlay for opacity
// https://stackoverflow.com/questions/21646738/convert-hex-to-rgba
function hexToRGB(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${(alpha / 100).toFixed(2)})`;
}

export const FaqHome = memo(function FaqHome({
  actions: { handleBackToStore, handleCollectionSelected },
  brandKit,
  theme,
  collections,
  searchViewProps,
  storeName,
}: {
  actions: {
    handleBackToStore: () => void;
    handleCollectionSelected: (collectionId: string) => void;
  };
  brandKit?: BrandKit;
  theme: IFaqTheme;
  collections: IFaqCollection[];
  searchViewProps: FaqSearchViewProps;
  storeName: string;
}) {
  const isMobile = useIsMobile();

  return (
    <>
      {isMobile && (
        <div className={homeCss.mobileBackButton}>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={LeftArrowIcon}
            onClick={handleBackToStore}
            size="md"
            text={`Back to ${storeName}`}
          />
        </div>
      )}
      <Flex
        dir="column"
        grow={1}
        style={{
          backgroundColor: `${brandKit && !theme.brandKitOverrides.pageBackgroundColor ? brandKit.colors.background : theme.pageBackgroundColor}`,
        }}
      >
        <Flex
          className={homeCss.header}
          dir="column"
          gap="md"
          justify="space-between"
          position="relative"
          style={{
            backgroundImage: `url(${theme.homeBackgroundImageUrl})`,
            backgroundColor: `${hexToRGB(theme.overlayColor, theme.overlayOpacity)}`,
            backgroundBlendMode: "multiply",
          }}
          w="full"
        >
          <Flex
            align="center"
            className={homeCss.headerContent}
            dir="row"
            justify="space-between"
            w="full"
          >
            <div style={{ color: `${theme.headlineTextColor}` }}>
              <Header
                className={homeCss.headline}
                fontSize="md"
                fontWeight="bold"
                textColor="inherit"
              >
                {theme.headlineText}
              </Header>
            </div>

            {!isMobile && (
              <RedoButton
                hierarchy="secondary"
                IconLeading={UpLeftArrowIcon}
                onClick={handleBackToStore}
                size="md"
                text={`Back to ${storeName}`}
              />
            )}
          </Flex>
          <Flex display="block" p="xl" w="full">
            <FaqSearchView {...searchViewProps} />
          </Flex>
        </Flex>
        <Flex justify="center" w="full">
          <Flex className={homeCss.pageContent} wrap="wrap">
            {collections.map((collection) => (
              <div
                className={homeCss.collectionCard}
                key={collection._id}
                onClick={() => handleCollectionSelected(collection._id)}
              >
                <SettingsCard
                  buttonSection={
                    <RedoButton IconLeading={RightArrowIcon} size="xs" />
                  }
                  customBackgroundColor={theme.collectionBackgroundColor}
                  customBorderColor={theme.collectionBorderColor}
                  customBorderRadius={theme.collectionCornerRadius}
                  description={
                    theme.displayCollectionSubtext ? collection.description : ""
                  }
                  Logo={theme.displayCollectionIcon ? CubeIcon : undefined}
                  name={collection.title}
                />
              </div>
            ))}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
});
